import { Component, ContentChild, EventEmitter, Inject, Input, Output, TemplateRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { API_BASE_URL_SERVICE } from '../../../kiene-core.config';
import { KieneBackendApiService } from '../../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../services/message-service/message.service';
import { ConfirmDialogInput, ConfirmDialogResult } from '../../dialogs/confirm-dialog/confirm-dialog';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { EnterValueDialogComponent } from '../../dialogs/enter-value-dialog/enter-value-dialog.component';
import { EnterValueInput, EnterValueResponse } from '../../dialogs/enter-value-dialog/enter-value-response';
import { GruppeTier } from '../../gruppe-tier/classes/gruppe-tier';
import { GruppeTierGewichteFestlegenDialogComponent } from '../../gruppe-tier/gruppe-tier-gewichte-festlegen-dialog/gruppe-tier-gewichte-festlegen-dialog.component';
import { GruppeTierBearbeitenDialogComponent } from '../../gruppe-tier/gruppe-tier-bearbeiten-dialog/gruppe-tier-bearbeiten-dialog.component';
import { GruppeTierBemerkungDialogComponent } from '../../gruppe-tier/gruppe-tier-bemerkung-dialog/gruppe-tier-bemerkung-dialog.component';
import { GruppeTierDetailsDialogComponent } from '../../gruppe-tier/gruppe-tier-details-dialog/gruppe-tier-details-dialog.component';
import { GruppeTierHilfeDialogComponent } from '../../gruppe-tier/gruppe-tier-hilfe-dialog/gruppe-tier-hilfe-dialog.component';
import { GruppeTierReklamierenDialogComponent } from '../../gruppe-tier/gruppe-tier-reklamieren-dialog/gruppe-tier-reklamieren-dialog.component';
import { GruppeTierTodMeldenDialogComponent } from '../../gruppe-tier/gruppe-tier-tod-melden-dialog/gruppe-tier-tod-melden-dialog.component';
import { GruppeTierService } from '../../gruppe-tier/gruppe-tier.service';
import { HIT_DATUM_GESPERRT_TEXT, istHitMeldungLaufend } from '../../gruppe-tier/hit-meldung-status.util';
import { GruppenService } from '../../gruppen/gruppen.service';
import { KieneUtils } from '../../../utils/KieneUtils';

@Component({
    selector: 'kiene-gruppe-tier-card',
    templateUrl: './gruppe-tier-card.component.html',
    styleUrls: ['./gruppe-tier-card.component.scss'],
})
export class GruppeTierCardComponent {
    @Input('tier') tier: GruppeTier;

    @Input('disableCardClickDetails') disableCardClickDetails = false;
    @Input('isSelected') isSelected = false;
    @Input('suppressContextMenu') suppressContextMenu = false;

    @Input('hideAllButtons') hideAllButtons = false;
    @Input('hideDetailsButton') hideDetailsButton = false;
    @Input('hideTodmeldungButton') hideTodmeldungButton = false;
    @Input('hideHitMeldenButton') hideHitMeldenButton = false;
    @Input('hideLoeschenButton') hideLoeschenButton = false;
    @Input('hideReklamationButton') hideReklamationButton = false;
    @Input('hideBemerkungButton') hideBemerkungButton = false;
    @Input('hideBearbeitenButton') hideBearbeitenButton = false;
    @Input('hideHilfeButton') hideHilfeButton = false;
    @Input('showGewichteFestlegen') showGewichteFestlegen = false;
    @Input('gewichteFestlegenDisabled') gewichteFestlegenDisabled = false;

    // Tierinfos
    @Input('hideStatusInfos') hideStatusInfos = false;
    @Input('hideTranspondernr') hideTranspondernr = false;
    @Input('hideGeschlechtRasse') hideGeschlechtRasse = false;
    @Input('hideGruppe') hideGruppe = false;
    @Input('hideKunde') hideKunde = true;
    @Input('hideAbteil') hideAbteil = false;
    @Input('hideGewicht') hideGewicht = false;
    @Input('hideAlter') hideAlter = false;
    @Input('hideEinstallungAbgang') hideEinstallung = false;
    @Input('hideAbgang') hideAbgang = false;
    @Input('hideTodesdatum') hideTodesdatum = false;
    @Input('hideGrundEntfernung') hideGrundEntfernung = false;
    @Input('hideReklamationen') hideReklamationen = false;
    @Input('hideBemerkung') hideBemerkung = false;

    @Output('reload') reload: EventEmitter<boolean> = new EventEmitter();

    @ContentChild(TemplateRef) matButtonTemplatesCard: TemplateRef<any>;

    constructor(
        private readonly dialog: MatDialog,
        private readonly api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly gruppeTierService: GruppeTierService,
        private readonly messageService: MessageService,
        public gruppenService: GruppenService,
    ) {}

    showDetails(tier: GruppeTier, event?: MouseEvent) {
        if (event && this.disableCardClickDetails) {
            return;
        }

        const dialogRef = this.dialog.open(GruppeTierDetailsDialogComponent, {
            width: '90vw',
            minWidth: '90vw',
            data: { tier: tier },
        });
        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.reload.emit(res);
            }
        });
    }

    onContextMenu(event: MouseEvent): void {
        if (!this.suppressContextMenu) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
    }

    isEinstalldatumHitGesperrt(tier: GruppeTier): boolean {
        return istHitMeldungLaufend(tier?.status_id_einstallen);
    }

    isAbgangsdatumHitGesperrt(tier: GruppeTier): boolean {
        return istHitMeldungLaufend(tier?.status_id_abgang);
    }

    todmeldung(tier: GruppeTier) {
        const dialogRef = this.dialog.open(GruppeTierTodMeldenDialogComponent, {
            width: '90vw',
            minWidth: '90vw',
            data: { tier: tier },
        });
        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.reload.emit(res);
                this.tier = res;
            }
        });
    }

    gewichteFestlegen(): void {
        if (this.gewichteFestlegenDisabled || !this.tier) {
            return;
        }
        const dialogRef = this.dialog.open(GruppeTierGewichteFestlegenDialogComponent, {
            width: '500px',
            maxWidth: '95vw',
            data: { gruppeId: this.tier.gruppe_id, gruppeTiere: [this.tier] },
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.reload.emit(true);
            }
        });
    }

    tierBearbeiten(tier: GruppeTier) {
        // TODO einstalldatum und ausstalldatum mit hinzufügen
        // Hinweis wenn Datum sich geändert hat das sich dadurch auch Daten in HIT ändern
        // Vor normalen Update ggf. HIT-Update-Route aufrufen

        const dialogRef = this.dialog.open(GruppeTierBearbeitenDialogComponent, {
            width: '90vw',
            minWidth: '90vw',
            data: { tier: tier },
        });
        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.reload.emit(res);
                this.tier = res;
            }
        });
    }

    loeschen(t: GruppeTier) {
        const input = new ConfirmDialogInput();
        input.headline = `Tier ${t.ohrmarke_notnull} löschen?`;
        input.text =
            'Wollen Sie dieses Tier wirklich löschen? Wenn ein Zugang über vetvet an HIT gemeldet wurden, wird dieser ebenfalls storniert!';
        input.okButton = 'Löschen';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    this.gruppeTierService.deleteTier(t).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Tier erfolgreich gelöscht');
                            this.reload.emit();
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler beim Löschen des Tiers', err);
                        },
                    });
                }
            },
        });
    }

    reklamationHinzufuegen(t: GruppeTier) {
        const dialogRef = this.dialog.open(GruppeTierReklamierenDialogComponent, {
            width: '760px',
            maxWidth: '95vw',
            maxHeight: '85vh',
            autoFocus: false,
            data: { tier: t },
        });

        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.reload.emit(res);
            }
        });
    }

    bemerkungHinzufuegen(tier: GruppeTier) {
        const dialogRef = this.dialog.open(GruppeTierBemerkungDialogComponent, {
            width: '90vw',
            minWidth: '90vw',
            data: { tier: tier },
        });

        dialogRef.afterClosed().subscribe(res => {
            if (res) {
                this.reload.emit(res);
            }
        });
    }

    openHelp() {
        this.dialog.open(GruppeTierHilfeDialogComponent, { width: '90vw', minWidth: '90vw' });
    }

    addOhrmarkeGescannt() {
        this.gruppeTierService.ohrmarkeAlsGescanntMarkieren(this.tier?.gruppe_id, [this.tier]).subscribe({
            next: (result: boolean) => {
                if (result === true) {
                    this.reload.emit(true);
                }
            },
        });
    }

    getIconStatusClass(status_id: number) {
        const id = Number(status_id);
        if (id === 142 || id === 145) {
            return 'icon-status--checked';
        }
        if (id === 141 || id === 144) {
            return 'icon-status--wait';
        }
        if (id === 143) {
            return 'icon-status--error';
        }
        return 'icon-status--unchecked'; // 140 und undefined etc.
    }

    gruppeAendern() {
        this.gruppeTierService.openGruppeAuswaehlenDialog([this.tier]).subscribe({
            next: neueGruppe => {
                if (neueGruppe) {
                    this.reload.emit(true);
                }
            },
        });
    }

    einstalldatumAendern(gt: GruppeTier) {
        if (istHitMeldungLaufend(gt?.status_id_einstallen)) {
            this.messageService.alertMessage(HIT_DATUM_GESPERRT_TEXT);
            return;
        }
        const input = new EnterValueInput();
        input.title = 'Einstalldatum ändern';
        input.text =
            'Es wird das Einstalldatum für ' +
            gt?.ohrmarke +
            '  geändert. Die HIT Zugangsmeldung wird ebenfalls aktualisiert.';
        input.okButton = 'Ändern';
        input.buttonColor = 'accent';
        input.addInputValue('Neues Einstalldatum', 'date');

        const dialogRef = this.dialog.open(EnterValueDialogComponent, { data: input });

        dialogRef.afterClosed().subscribe({
            next: (result: EnterValueResponse) => {
                if (result?.result && result?.getValue(0)) {
                    const einstalldatumNeu: Date = result.getValue(0);
                    this.gruppeTierService.einstalldatumHitAendern([gt], einstalldatumNeu).subscribe({
                        next: () => {
                            this.messageService.infoMessage(
                                'Das Einstalldatum wurde erfolgreich geändert! Die Aktualisierung in der HIT Datenbank kann bis zu fünf Minuten dauern.',
                            );
                            this.reload.emit(true);
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler!', err);
                            this.reload.emit(true);
                        },
                    });
                }
            },
        });
    }

    abgangsdatumAendern(gt: GruppeTier) {
        if (istHitMeldungLaufend(gt?.status_id_abgang)) {
            this.messageService.alertMessage(HIT_DATUM_GESPERRT_TEXT);
            return;
        }
        const input = new EnterValueInput();
        input.title = 'Abgangsdatum ändern';
        input.text =
            'Es wird das Abgangsdatum für ' +
            gt?.ohrmarke +
            ' geändert. Die HIT Abgangsmeldung wird ebenfalls aktualisiert.';
        input.okButton = 'Ändern';
        input.buttonColor = 'accent';
        input.addInputValue('Neues Abgangsdatum', 'date');

        const dialogRef = this.dialog.open(EnterValueDialogComponent, { data: input });

        dialogRef.afterClosed().subscribe({
            next: (result: EnterValueResponse) => {
                if (result?.result && result?.getValue(0)) {
                    const abgangsdatumNeu: Date = result.getValue(0);
                    this.gruppeTierService.abgangsdatumHitAendern([gt], abgangsdatumNeu).subscribe({
                        next: () => {
                            this.messageService.infoMessage(
                                'Das Abgangsdatum wurde erfolgreich geändernt! Die Aktualisierung in der HIT Datenbank kann bis zu fünf Minuten dauern.',
                            );
                            this.reload.emit(true);
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler!', err);
                            this.reload.emit(true);
                        },
                    });
                }
            },
        });
    }

    abgangStornieren(gruppeTier: GruppeTier) {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                headline: 'Abgang stornieren?',
                text: 'Wollen Sie den Abgang des Tieres wirklich stornieren?',
                okButton: 'Stornieren',
                cancelButton: 'Abbrechen',
            },
        });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (result?.confirmed) {
                    this.gruppeTierService.abgangStornieren(gruppeTier).subscribe({
                        next: () => {
                            this.messageService.infoMessage('Stornierungsmeldung wurde erzeugt!');
                            this.reload.emit(true);
                        },
                        error: err => {
                            this.messageService.errorMessage('Fehler!', err);
                            this.reload.emit(true);
                        },
                    });
                }
            },
        });
    }

    geschlechtIcon(tier: GruppeTier | null | undefined): 'male' | 'female' | 'transgender' {
        if (KieneUtils.sameId(tier?.geschlecht_id, 1)) {
            return 'male';
        }
        if (KieneUtils.sameId(tier?.geschlecht_id, 2)) {
            return 'female';
        }
        return 'transgender';
    }
}
